import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { Resource } from '../../common/model/type/resource.enum';
import { ARTICLE_LIST_FILTERS } from '../../common/model/ui/article-list-filters';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { getColumnSortOptions } from '../../common/utils/services/list-filter-utils';
import { getListRange } from '../../common/utils/services/order-list-utils';
import Colours from '../../design-system/colours';
import { useAdminArticleMutations } from '../../hooks/use-admin-article-mutations';
import { useArticleListColumns } from '../../hooks/use-article-list-columns';
import useAuthorized from '../../hooks/use-authorized';
import { useListNavigationState } from '../../hooks/use-list-navigation-state';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import ArticlesService from '../../services/articles.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvArticleList from '../UI/organisms/OvArticleList';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

const AdminArticleList: FC = () => {
  const { t } = useTranslation();
  const limit = ArticlesService.ARTICLE_LIST_LIMIT;
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const applicationOperations = useAuthorized(Resource.Application);
  const articleOperations = useAuthorized(Resource.Article);
  const articleCreateAllowed =
    articleOperations.create || applicationOperations.supervise;

  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(ARTICLE_LIST_FILTERS);

  const query = queryString.stringify({
    offset,
    limit,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
    ...filterQueryParams,
  });

  const {
    data: paginatedArticles,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-articles', query],
    queryFn: () => ArticlesService.getArticles(query),
    placeholderData: keepPreviousData,
  });

  const articles = paginatedArticles?.docs ?? [];
  const total = paginatedArticles?.total ?? 0;

  const { deleteArticle } = useAdminArticleMutations();

  const getNavigationState = useListNavigationState();
  const columns = useArticleListColumns(getNavigationState, setDeleteTargetId);

  const handleDeleteArticle = () => {
    if (deleteTargetId) {
      deleteArticle.mutate(deleteTargetId);
      setDeleteTargetId(null);
    }
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('articles.resultRange', {
      ...getListRange(offset, limit, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('articles.states.errorTitle')}
          description={t('articles.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (articles.length > 0) {
      return (
        <OvArticleList
          articles={articles}
          columns={columns}
          getNavigationState={getNavigationState}
          orderBy={orderBy?.order_by ?? ''}
          orderDir={orderBy?.order_dir ?? ''}
          onSort={changeOrderBy}
        />
      );
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('articles.states.noResultsTitle')}
          description={t('articles.emptyArticleListAfterFiltering')}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<ArticleIcon />}
        title={t('articles.states.emptyTitle')}
        description={t('articles.emptyArticleList')}
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('common.articles')}
      icon={<ArticleIcon />}
      headerAction={
        articleCreateAllowed && (
          <NavLink to="/articles/create">
            <OvCompactButton icon={<AddCircleIcon />}>
              {t('common.actions.addNew')}
            </OvCompactButton>
          </NavLink>
        )
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={getColumnSortOptions(columns, t)}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      context={
        deleteArticle.error && (
          <OvErrorMessage message={getAxiosErrorMessage(deleteArticle.error)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={(_, page) => setOffset((page - 1) * limit)}
      isBusy={isFetching || deleteArticle.isPending}
    >
      {renderContent()}

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={!!deleteTargetId}
        onCancel={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteArticle}
        description="articles.actions.permanentArticleDelete"
        title="common.areYouSure"
      />
    </OvRecordListPage>
  );
};

export default AdminArticleList;

const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
